import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  ArrowLeft, Plus, Search, Edit, Trash2, Calendar, Clock, PoundSterling, User, Home
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const BookingList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerFilter = searchParams.get('customer');
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [showPastBookings, setShowPastBookings] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/bookings`;
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (customerFilter) params.append('customer_id', customerFilter);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, searchQuery, customerFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchBookings();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchBookings]);

  // Filter and sort bookings
  const filteredAndSortedBookings = React.useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    
    // Filter out past bookings if toggle is off
    let filtered = showPastBookings 
      ? bookings 
      : bookings.filter(b => b.return_date >= today);
    
    // Sort by collection date (nearest first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.collection_date);
      const dateB = new Date(b.collection_date);
      return dateA - dateB;
    });
  }, [bookings, showPastBookings]);

  const pastBookingsCount = React.useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return bookings.filter(b => b.return_date < today).length;
  }, [bookings]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchBookings();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchBookings]);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setBookings(prev => prev.filter(b => b.booking_id !== deleteId));
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getBookingStatus = (booking) => {
    const today = new Date().toISOString().split('T')[0];
    const collection = booking.collection_date;
    const returnDate = booking.return_date;

    if (today < collection) return { label: 'Upcoming', color: 'bg-blue-500/20 text-blue-400' };
    if (today > returnDate) return { label: 'Completed', color: 'bg-green-500/20 text-green-400' };
    return { label: 'In Progress', color: 'bg-[#c9a55a]/20 text-[#c9a55a]' };
  };

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="booking-list-page">
      {/* Header */}
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
              title="Home"
            >
              <Home className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-[#f5f3ed]">
              Bookings
              {customerFilter && <span className="text-[#c9a55a] text-lg ml-2">(Filtered)</span>}
            </h1>
          </div>
          <Button
            onClick={() => navigate('/admin/bookings/new')}
            className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
            data-testid="add-booking-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Booking
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search and Filter */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#f5f3ed]/50" />
            <Input
              type="text"
              placeholder="Search bookings by customer name or date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#2d5f3f]/30 border-[#2d5f3f] text-[#f5f3ed] placeholder:text-[#f5f3ed]/50"
              data-testid="booking-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPastBookings(!showPastBookings)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                showPastBookings 
                  ? 'bg-[#c9a55a]/20 border-[#c9a55a] text-[#c9a55a]' 
                  : 'bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]/70'
              }`}
              data-testid="toggle-past-bookings"
            >
              <Clock className="w-4 h-4" />
              <span className="text-sm">Past Bookings</span>
              {pastBookingsCount > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${showPastBookings ? 'bg-[#c9a55a]/30' : 'bg-[#2d5f3f]'}`}>
                  {pastBookingsCount}
                </span>
              )}
            </button>
          </div>
          {customerFilter && (
            <Button
              variant="outline"
              onClick={() => navigate('/admin/bookings')}
              className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10"
            >
              Clear Filter
            </Button>
          )}
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#c9a55a]"></div>
          </div>
        ) : filteredAndSortedBookings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-[#f5f3ed]/30 mx-auto mb-4" />
            <p className="text-[#f5f3ed]/50">
              {searchQuery || customerFilter 
                ? 'No bookings found matching your criteria' 
                : showPastBookings 
                  ? 'No bookings yet'
                  : 'No upcoming bookings'}
            </p>
            {!showPastBookings && pastBookingsCount > 0 && (
              <button
                onClick={() => setShowPastBookings(true)}
                className="mt-2 text-[#c9a55a] text-sm hover:underline"
              >
                Show {pastBookingsCount} past booking{pastBookingsCount !== 1 ? 's' : ''}
              </button>
            )}
            <Button
              onClick={() => navigate('/admin/bookings/new')}
              className="mt-4 bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Booking
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredAndSortedBookings.map(booking => {
              const status = getBookingStatus(booking);
              
              return (
                <div
                  key={booking.booking_id}
                  className="bg-[#2d5f3f]/30 rounded-xl p-5 border border-[#2d5f3f] hover:border-[#c9a55a]/50 transition-colors"
                  data-testid={`booking-card-${booking.booking_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {booking.display_id && (
                          <span className="bg-[#c9a55a]/20 text-[#c9a55a] px-2 py-1 rounded text-sm font-mono">
                            {booking.display_id}
                          </span>
                        )}
                        <h3 className="text-lg font-semibold text-[#f5f3ed]">
                          {booking.customer_name}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <div className="flex items-center gap-2 text-[#f5f3ed]/70 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>Collection: {formatDate(booking.collection_date)} at {booking.collection_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#f5f3ed]/70 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>Return: {formatDate(booking.return_date)} at {booking.return_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#f5f3ed]/70 text-sm">
                          <PoundSterling className="w-4 h-4" />
                          <span>Total: £{booking.total_cost?.toFixed(2) || '0.00'}</span>
                        </div>
                        {booking.balance_due > 0 && (
                          <div className="flex items-center gap-2 text-[#c9a55a] text-sm">
                            <PoundSterling className="w-4 h-4" />
                            <span>Balance Due: £{booking.balance_due.toFixed(2)}</span>
                          </div>
                        )}
                      </div>

                      {/* Payment Status Indicators */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className={`text-xs px-2 py-1 rounded ${booking.booking_deposit_received ? 'bg-green-500/20 text-green-400' : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/50'}`}>
                          Booking Deposit
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${booking.total_booking_fee_received ? 'bg-green-500/20 text-green-400' : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/50'}`}>
                          Total Fee
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${booking.security_deposit_received ? 'bg-green-500/20 text-green-400' : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/50'}`}>
                          Security Deposit
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${booking.security_deposit_returned ? 'bg-blue-500/20 text-blue-400' : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/50'}`}>
                          Security Returned
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/admin/bookings/${booking.booking_id}`)}
                        className="text-[#c9a55a] hover:bg-[#c9a55a]/10"
                        data-testid={`edit-booking-${booking.booking_id}`}
                      >
                        <Edit className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(booking.booking_id)}
                        className="text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-booking-${booking.booking_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1a1f1a] border-[#2d5f3f]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f5f3ed]">Delete Booking?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#f5f3ed]/70">
              This will permanently delete this booking. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BookingList;
